import type { ProviderInstrumentsAction } from './actions';
import type { InstrumentDialogState, PendingInstrumentRemovalState, ProviderInstrumentsState } from './types';
import { isEqual } from 'es-toolkit';
import type { ProviderInstruments } from 'src/admin/LAB/contracts/providers';

/**
 * A refetch can change the entry an open edit refers to. Drop the dialog if the entry is gone;
 * if it changed, refresh the original and return to the form so the new reason is reviewed.
 */
function refreshDialog(
    dialog: InstrumentDialogState | null,
    instruments: ProviderInstruments,
): InstrumentDialogState | null {
    if (dialog === null || dialog.original === null || dialog.isSaving) return dialog;
    const entry = instruments[dialog.original.symbol];
    if (!entry) return null;
    const original = { ...entry, symbol: dialog.original.symbol };
    if (isEqual(original, dialog.original)) return dialog;
    // An untouched draft follows the refreshed reason; an edited one is kept.
    const reason = dialog.draft.reason === dialog.original.reason ? original.reason : dialog.draft.reason;
    return { ...dialog, original, draft: { ...dialog.draft, reason }, step: 'form' };
}

function refreshRemoval(
    pending: PendingInstrumentRemovalState | null,
    instruments: ProviderInstruments,
): PendingInstrumentRemovalState | null {
    if (pending === null || pending.isRemoving) return pending;
    const entry = instruments[pending.entry.symbol];
    if (!entry) return null;
    const refreshed = { ...entry, symbol: pending.entry.symbol };
    return isEqual(refreshed, pending.entry) ? pending : { ...pending, entry: refreshed };
}

export function providerInstrumentsReducer(
    state: ProviderInstrumentsState,
    action: ProviderInstrumentsAction,
): ProviderInstrumentsState {
    switch (action.type) {
        case 'SET_PROVIDER': {
            if (state.providerId === action.providerId) {
                if (isEqual(state.instruments, action.instruments)) return state;
                return {
                    ...state,
                    instruments: action.instruments,
                    dialog: refreshDialog(state.dialog, action.instruments),
                    pendingRemoval: refreshRemoval(state.pendingRemoval, action.instruments),
                };
            }
            // A different provider: open dialogs belong to the old one.
            return {
                providerId: action.providerId,
                instruments: action.instruments,
                dialog: null,
                pendingRemoval: null,
            };
        }
        case 'OPEN_DIALOG':
            if (state.dialog?.isSaving) return state;
            return { ...state, dialog: action.dialog };
        case 'CLOSE_DIALOG':
            // Ignored while the write is in flight so the dialog can't vanish out from under it.
            if (state.dialog === null || state.dialog.isSaving) return state;
            return { ...state, dialog: null };
        case 'PATCH_DRAFT': {
            if (state.dialog === null || state.dialog.isSaving) return state;
            return {
                ...state,
                dialog: {
                    ...state.dialog,
                    draft: { ...state.dialog.draft, ...action.patch },
                    error: null,
                },
            };
        }
        case 'SET_DIALOG_STEP': {
            if (state.dialog === null || state.dialog.isSaving) return state;
            return {
                ...state,
                dialog: { ...state.dialog, step: action.step, error: null },
            };
        }
        case 'START_SAVE': {
            if (state.dialog === null) return state;
            return {
                ...state,
                dialog: { ...state.dialog, isSaving: true, saveToken: action.token, error: null },
            };
        }
        case 'FAIL_SAVE': {
            // A completion for another write, or for a dialog opened since, is stale.
            if (state.dialog?.saveToken !== action.token) return state;
            return {
                ...state,
                dialog: { ...state.dialog, isSaving: false, saveToken: null, error: action.message },
            };
        }
        case 'FINISH_SAVE':
            if (state.dialog?.saveToken !== action.token) return state;
            return { ...state, instruments: action.instruments, dialog: null };
        case 'REQUEST_REMOVE':
            if (state.pendingRemoval?.isRemoving) return state;
            return {
                ...state,
                pendingRemoval: {
                    entry: action.entry,
                    token: action.token,
                    isRemoving: false,
                    error: null,
                },
            };
        case 'CANCEL_REMOVE':
            if (state.pendingRemoval === null || state.pendingRemoval.isRemoving) return state;
            return { ...state, pendingRemoval: null };
        case 'START_REMOVE':
            if (state.pendingRemoval === null) return state;
            return {
                ...state,
                pendingRemoval: {
                    ...state.pendingRemoval,
                    isRemoving: true,
                    error: null,
                },
            };
        case 'FAIL_REMOVE':
            // A completion for another confirmation is stale.
            if (state.pendingRemoval?.token !== action.token) return state;
            return {
                ...state,
                pendingRemoval: {
                    ...state.pendingRemoval,
                    isRemoving: false,
                    error: action.message,
                },
            };
        case 'FINISH_REMOVE':
            if (state.pendingRemoval?.token !== action.token) return state;
            return {
                ...state,
                instruments: action.instruments,
                pendingRemoval: null,
            };

        default: {
            const _exhaustiveCheck: never = action;
            return state;
        }
    }
}
